import pickBy from 'lodash/pickBy';

import { FilterByTypeType, RefineOptionKeyType, URLObjectType } from './types';
import {
  filterByTypeOptionKeys,
  sloFilterOptionKeys,
  capabilityFilterOptionKeys,
} from './constants';

export type ReviseFilterOptionsType = {
  query: any;
  disabledOptionKeys: RefineOptionKeyType[];
};

/**
 * Get disabled filter options based on current url object e.g. when SLO type is
 * checked but Capability type is not, we want to disallow the capability
 * options below
 * @see https://hello.atlassian.net/browse/PSREAU-1496
 *
 * @param urlObject
 * @returns
 */
export const getDisabledOptions = (urlObject: URLObjectType): RefineOptionKeyType[] => {
  let disabledOptionKeys: RefineOptionKeyType[] = [];

  // Get all the currently checked options in refine panel. This includes the
  // checked type options and filter options
  const checkedOptions = new Set(Object.keys(pickBy(urlObject, (x: string) => x === 'true')));

  // If nothing is checked in type section, it's fine to leave any filter
  // options below
  if (
    !filterByTypeOptionKeys.some((filterByTypeOptionKey: FilterByTypeType) =>
      checkedOptions.has(filterByTypeOptionKey)
    )
  ) {
    return disabledOptionKeys;
  }

  // Now we clearly know there is at least one type checked and unchecked types
  // are excluded from search results
  filterByTypeOptionKeys.forEach((filterByTypeOptionKey: FilterByTypeType) => {
    // Current type is excluded from search results, hence we should disable and
    // uncheck its filters
    if (!checkedOptions.has(filterByTypeOptionKey)) {
      switch (filterByTypeOptionKey) {
        case FilterByTypeType.BySlo: {
          disabledOptionKeys = disabledOptionKeys.concat(sloFilterOptionKeys);
          break;
        }
        case FilterByTypeType.ByCapability: {
          disabledOptionKeys = disabledOptionKeys.concat(capabilityFilterOptionKeys);
          break;
        }
      }
    }
  });

  return disabledOptionKeys;
};

/**
 * This revises filter options, returns a combination of updated query state
 * and disabled filter options. This function internally just constructs the
 * next urlObject state based on check or uncheck action, and calls
 * getDisabledOptions.
 *
 * @param value
 * @param checked
 * @param urlObject
 * @returns
 */
export const reviseFilterOptions = (
  value: RefineOptionKeyType,
  checked: boolean,
  urlObject: URLObjectType
): ReviseFilterOptionsType => {
  // Build the basic push in query from the check / uncheck action
  const query = { [value]: checked || undefined, page: undefined };

  // Apply the action to build the next url object
  const urlObjectNext = { ...urlObject };
  if (checked) {
    urlObjectNext[value] = 'true';
  } else {
    delete urlObjectNext[value];
  }

  const disabledOptionKeys: RefineOptionKeyType[] = getDisabledOptions(urlObjectNext);
  disabledOptionKeys.forEach((key: RefineOptionKeyType) => {
    // set undefined is how useQueryParams removes param
    query[key] = undefined;
  });

  return { query, disabledOptionKeys };
};

/**
 * This gives a flat page numbers array based on total page count
 */
export const getPagesFromTotalPage = (totalPages: number | undefined): number[] => {
  if (!totalPages) return [];
  return Array(Math.ceil(totalPages) || 1)
    .fill(null)
    .map((_, index) => index + 1);
};
